/**
 * EntrepreneurOnboarding — Streaming onboarding chat for entrepreneur tier.
 * Sets up business spaces via AI conversation.
 * No Phase 1 check-in — goes straight to streaming chat.
 * Watches for complete_entrepreneur_onboarding tool result.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Briefcase, Volume2, VolumeX } from 'lucide-react';
import { startEntrepreneurOnboarding, sendMessageStream, sendMessage, setVoiceDefaultOn } from '../lib/api';
import { useVoiceConversation } from '../lib/useVoiceConversation';
import VoiceInput from './VoiceInput';

export default function EntrepreneurOnboarding({ onComplete, voiceId, voiceDefaultOn = true }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [dismissedContinue, setDismissedContinue] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatStarted = useRef(false);

  // Entrepreneur tier is implicit here (this component only renders for that tier)
  const voice = useVoiceConversation({
    enabled: true,
    defaultOn: voiceDefaultOn,
    onTranscribed: (text) => handleSendWithText(text),
    voiceId,
    onVoiceModeChange: (next) => { setVoiceDefaultOn(next).catch(() => {}); },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start entrepreneur onboarding session immediately
  useEffect(() => {
    (async () => {
      try {
        const res = await startEntrepreneurOnboarding();
        if (res.already_completed) {
          onComplete?.();
          return;
        }
        if (res.session_id) {
          setSessionId(res.session_id);
        }
      } catch (err) {
        console.error('Failed to start entrepreneur onboarding:', err);
        setError('Failed to start setup. Please refresh and try again.');
      } finally {
        setSessionLoading(false);
      }
    })();
  }, [onComplete]);

  // Auto-send opening message once session is ready
  useEffect(() => {
    if (sessionId && !chatStarted.current) {
      chatStarted.current = true;
      handleSendWithText("I'm ready to set up my business spaces.");
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendWithText = async (text) => {
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      await handleSendStreaming(updatedMessages);
    } catch {
      try {
        await handleSendNonStreaming(updatedMessages);
      } catch {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Connection error. Please try again.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleSendStreaming = async (updatedMessages) => {
    let assistantText = '';
    const assistantMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString(), thinking: true };
    setMessages([...updatedMessages, assistantMsg]);
    setStreaming(true);
    voice.beginUtterance();

    let complete = false;

    await sendMessageStream(updatedMessages, sessionId, (event) => {
      switch (event.type) {
        case 'text_delta':
          assistantText += event.text;
          voice.feedTextDelta(event.text);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText, thinking: false };
            return updated;
          });
          break;
        case 'tool_start':
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              updated[updated.length - 1] = { ...last, thinking: true, toolName: event.name };
            }
            return updated;
          });
          break;
        case 'tool_result':
          if (event.name === 'complete_entrepreneur_onboarding' && !event.is_error) {
            complete = true;
          }
          break;
        case 'done':
          if (complete) {
            setOnboardingDone(true);
          }
          break;
        case 'error':
          assistantText += (assistantText ? '\n\n' : '') + `Error: ${event.message}`;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText };
            return updated;
          });
          break;
        default:
          break;
      }
    });

    voice.endUtterance();
  };

  const handleSendNonStreaming = async (updatedMessages) => {
    const result = await sendMessage(updatedMessages, sessionId);
    if (result.response) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString()
      }]);
    }
    // Mirror the streaming path's complete_entrepreneur_onboarding detection.
    if (result.onboarding_complete) {
      setOnboardingDone(true);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    handleSendWithText(text);
  };

  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Loading state
  if (sessionLoading || (!sessionId && !error)) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-blue-400 mb-3" />
        <p className="text-sm text-slate-400">Setting up your business world...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <p className="text-sm text-red-400 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-indigo-950/20">
        <Briefcase size={16} className="text-indigo-400" />
        <span className="text-sm font-medium text-indigo-400">Setting Up Your Business World</span>
        {voice.available && (
          <button
            onClick={voice.toggleVoiceMode}
            title={voice.voiceMode ? 'Voice mode on' : 'Voice mode off'}
            aria-pressed={voice.voiceMode}
            className={`ml-auto flex items-center justify-center min-h-tap min-w-tap sm:min-h-0 sm:min-w-0 sm:w-8 sm:h-8 rounded-md transition-colors ${
              voice.voiceMode
                ? 'text-purple-300 bg-purple-900/30 hover:bg-purple-900/50'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
            }`}
          >
            {voice.voiceMode ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        )}
      </div>

      {voice.error && (
        <div className="px-4 py-2 bg-red-900/20 border-b border-red-900/40 text-xs text-red-300 flex items-center justify-between">
          <span>{voice.error}</span>
          <button onClick={voice.clearError} className="text-red-400 hover:text-red-200" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-blue-600/20 text-blue-100 border border-blue-800/30'
                : 'bg-slate-800/50 text-slate-200 border border-slate-700/50'
            }`}>
              {msg.thinking && !msg.content ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-xs">
                    {msg.toolName ? `Running ${msg.toolName}...` : 'Thinking...'}
                  </span>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}

        {loading && !streaming && (
          <div className="flex justify-start">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input / Completion prompt */}
      <div className="border-t border-slate-800 p-4">
        {onboardingDone ? (
          <div className="max-w-3xl mx-auto">
            {dismissedContinue ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-400">
                  Your spaces are ready. When you're set, daily planning is next.
                </p>
                <button
                  onClick={() => onComplete?.()}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                >
                  Start Planning
                </button>
              </div>
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-300">Your spaces are ready!</p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => onComplete?.()}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                  >
                    Let's Go
                  </button>
                  <button
                    onClick={() => setDismissedContinue(true)}
                    className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg px-6 py-2.5 transition-colors"
                  >
                    Not Yet
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : voice.voiceMode ? (
          <VoiceInput voice={voice} loading={loading} />
        ) : (
          <div className="flex gap-2 max-w-3xl mx-auto">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(e.target); }}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              rows={1}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg px-4 py-3 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
